import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { useParams } from '@hooks/useParams';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { OnSortColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { NotificationTypes } from '@dynamika-shared/types/subscription';
import {
  setCurrentPage,
  useMakePagination,
} from '@restore/paginationAndSort/paginationAndSort.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useTeamMembersListSlice } from '@restore/teamMembersList/teamMembersList.slice';
import { useClinicalTrialSiteRecipientsSlice } from '@restore/clinicalTrialSiteRecipients/clinicalTrialSiteRecipients.slice';
import { makeSiteRecipientsColumns } from './SiteRecipients.columns';
import { SiteRecipientsFilters } from './SiteRecipientsFilters';
import { SiteRecipientsSiteFilterOption } from './SiteRecipientsSiteFilter';
import { UploadRecipientsDrawer } from './UploadRecipientsDrawer';
import {
  buildSiteRecipientRows,
  paginateSiteRecipientRows,
  sortSiteRecipientRows,
} from './siteRecipients.utils';

const DEFAULT_SORT = 'fullName,asc';
const SEARCH_DEBOUNCE_MS = 500;

const SELECT_SITE_MESSAGE =
  'Select a site to view its notification recipients.';
const NO_RECIPIENTS_MESSAGE =
  'No notification recipients configured for this site.';
const NO_MATCHES_MESSAGE = 'No recipients match the applied criteria.';
const LOAD_ERROR_MESSAGE =
  'Notification recipients could not be loaded. Please try again later.';

export const ClinicalTrialNotificationsSiteRecipients = () => {
  const dispatch = useDispatch();
  const { id: clinicalTrialId } = useParams();

  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [searchParam, setSearchParam] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<NotificationTypes[]>([]);
  const [sortBy, setSortBy] = useState<string | undefined>(DEFAULT_SORT);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const debouncedSetSearchParam = useMemo(
    () => debounce(setSearchParam, SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => debouncedSetSearchParam.cancel(), [debouncedSetSearchParam]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    debouncedSetSearchParam(value);
  };

  const sites = useClinicalTrialSlice(state => state.data?.sites ?? []);

  const {
    recipients,
    loading,
    error,
    exporting,
    fetchSiteRecipients,
    resetSiteRecipients,
    exportSiteRecipients,
  } = useClinicalTrialSiteRecipientsSlice((state, actions) => ({
    recipients: state.data,
    loading: state.loading,
    error: state.error,
    exporting: state.exporting,
    fetchSiteRecipients: actions.fetchSiteRecipients,
    resetSiteRecipients: actions.resetSiteRecipients,
    exportSiteRecipients: actions.exportSiteRecipients,
  }));

  const { teamMembers, teamMembersLoading, fetchTeamMembers } =
    useTeamMembersListSlice((state, actions) => ({
      teamMembers: state.allData,
      teamMembersLoading: state.loading,
      fetchTeamMembers: actions.fetchTeamMembersListAll,
    }));

  useEffect(() => {
    if (!clinicalTrialId) return;

    fetchTeamMembers({ clinicalTrialId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId]);

  const rows = useMemo(
    () =>
      buildSiteRecipientRows({
        recipients,
        teamMembers: teamMembers ?? [],
      }),
    [recipients, teamMembers]
  );

  // A site missing either half cannot be offered: without an id there is
  // nothing to ask the backend for, without a code nothing to label the row.
  const siteOptions: SiteRecipientsSiteFilterOption[] = useMemo(
    () =>
      sites
        .filter(site => site?.id && site?.code)
        .map(site => ({ id: site.id as string, code: site.code as string })),
    [sites]
  );

  const selectedEventsKey = selectedEvents.join(',');

  /**
   * Nothing is loaded until a site is picked
   */
  useEffect(() => {
    if (!clinicalTrialId) return;

    if (!selectedSiteId) {
      resetSiteRecipients();

      return;
    }

    fetchSiteRecipients({
      clinicalTrialId,
      siteId: selectedSiteId,
      search: searchParam,
      notificationTypes: selectedEvents,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, selectedSiteId, searchParam, selectedEventsKey]);

  const sortedRows = useMemo(
    () => sortSiteRecipientRows(rows, sortBy),
    [rows, sortBy]
  );

  const columns = useMemo(() => makeSiteRecipientsColumns(), []);

  const pagination = useMakePagination(
    'notificationSiteRecipients',
    sortedRows.length > 0
  );

  const pageRows = useMemo(
    () =>
      paginateSiteRecipientRows(
        sortedRows,
        pagination.currentPage,
        pagination.pageSize
      ),
    [sortedRows, pagination.currentPage, pagination.pageSize]
  );

  // Back to page 1 whenever the result set changes shape or order, so the user
  // doesn't sit on an index that now holds different rows.
  useEffect(() => {
    dispatch(
      setCurrentPage({ listName: 'notificationSiteRecipients', newPage: 1 })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId, searchParam, selectedEventsKey, sortBy]);

  // Changing the site clears both refinements.
  const handleSelectedSiteChange = (siteId?: string) => {
    setSelectedSiteId(siteId);
    setSelectedEvents([]);

    setSearch('');
    debouncedSetSearchParam.cancel();
    setSearchParam('');
  };

  /**
   * Only the viewed site can show the result; with none picked there is
   * nothing to refresh. Refinements stay — an upload must not reset them.
   */
  // Stable so the drawer's action subscription is not torn down and
  // re-registered on every keystroke in the search box.
  const closeUpload = useCallback(() => setIsUploadOpen(false), []);

  const handleUploaded = useCallback(() => {
    if (!clinicalTrialId || !selectedSiteId) return;

    fetchSiteRecipients({
      clinicalTrialId,
      siteId: selectedSiteId,
      search: searchParam,
      notificationTypes: selectedEvents,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, selectedSiteId, searchParam, selectedEventsKey]);

  /**
   * Exports what the table shows — all filters optional server-side, so no
   * gate on a site; `searchParam` lags `search` by the debounce on purpose.
   */
  const handleExport = () => {
    if (!clinicalTrialId) return;

    exportSiteRecipients({
      clinicalTrialId,
      siteId: selectedSiteId,
      search: searchParam,
      notificationTypes: selectedEvents,
    });
  };

  const onSort: OnSortColumnType = (columnName, sortState) => {
    setSortBy(sortState ? `${columnName},${sortState}` : DEFAULT_SORT);
  };

  const isLoading = loading || teamMembersLoading;

  const emptyMessage = useMemo(() => {
    if (!selectedSiteId) return SELECT_SITE_MESSAGE;

    if (error) return LOAD_ERROR_MESSAGE;

    const hasRefinements = Boolean(search.trim()) || selectedEvents.length > 0;

    return hasRefinements ? NO_MATCHES_MESSAGE : NO_RECIPIENTS_MESSAGE;
  }, [selectedSiteId, error, search, selectedEvents]);

  return (
    <div className='flex flex-col gap-2 pt-3'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex flex-col'>
          <h3 className='font-sans text-2432 font-semibold text-black'>
            Recipients
          </h3>
          <p className='font-sans text-1424 font-medium text-spanishGray'>
            View and manage notification recipients for one or more sites
          </p>
        </div>

        {/* Beside the title, above the filters and not among them: a CSV
            spans sites, so neither action is narrowed by them and both stay
            available before one is picked. */}
        {clinicalTrialId && (
          <div className='flex shrink-0 items-center gap-1'>
            <Button
              data-testid='site-recipients-export-button'
              // Ant renders its spinner alongside our icon, so the icon stands
              // down for the duration rather than doubling up with it.
              iconType={exporting ? undefined : 'export'}
              iconClassName={'mr-0.5 h-2.5 w-2.5'}
              className='!border-chineseBlack !bg-blackOlive !pl-2 !pr-3 text-white hover:!border-dodgerBlue hover:!bg-dodgerBlue'
              disabled={exporting}
              loading={exporting}
              onClick={handleExport}
            >
              Export CSV
            </Button>

            <Button
              data-testid='site-recipients-upload-button'
              iconType='plus'
              iconClassName={'w-2 h-2'}
              type='primary'
              onClick={() => setIsUploadOpen(true)}
            >
              Upload
            </Button>
          </div>
        )}
      </div>

      <SiteRecipientsFilters
        sites={siteOptions}
        selectedSiteId={selectedSiteId}
        onSelectedSiteChange={handleSelectedSiteChange}
        search={search}
        onSearchChange={handleSearchChange}
        selectedEvents={selectedEvents}
        onEventsChange={setSelectedEvents}
        disabled={isLoading}
      />

      <Table
        columns={columns}
        data={error ? [] : pageRows}
        totalItems={error ? 0 : sortedRows.length}
        loading={isLoading}
        onSort={onSort}
        sortState={sortBy}
        pagination={pagination}
        styles={{
          table: sortedRows.length > 0 ? 'table-auto' : 'table-auto !min-w-0 w-fit',
        }}
        noContentMessage={
          <NoContent
            iconName={'team'}
            title={
              <span className='text-1424 text-spanishGray'>{emptyMessage}</span>
            }
          >
            <></>
          </NoContent>
        }
      />

      {clinicalTrialId && (
        <UploadRecipientsDrawer
          open={isUploadOpen}
          close={closeUpload}
          clinicalTrialId={clinicalTrialId}
          onUploaded={handleUploaded}
        />
      )}
    </div>
  );
};
